import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useContext, useState } from 'react';
import type { MenuPosition, Section } from '../types';

type PopupContextValue = {
  showPopup: () => void;
  hidePopup: () => void;
  setPopupItem: Dispatch<SetStateAction<MenuPosition | undefined>>;
  popupItem: MenuPosition | undefined;
  popupVisible: boolean;
  popupCurrentItemSection: MenuPosition[];
  setPopupCurrentItemSection: Dispatch<SetStateAction<MenuPosition[]>>;
  popupSections: Section[];
  setPopupSections: Dispatch<SetStateAction<Section[]>>;
};

export const PopupContext = createContext<PopupContextValue | null>(null);

export const usePopupContext = () => {
  const context = useContext(PopupContext);

  if (context === null) {
    throw new Error(`usePopupContext should be used within PopupContext`);
  }

  return context;
};

export function PopupProvider({ children }: PropsWithChildren) {
  const [show, setShow] = useState<boolean>(false);
  const [popupItem, setPopupItem] = useState<MenuPosition | undefined>(
    undefined
  );
  const [popupCurrentItemSection, setPopupCurrentItemSection] = useState<
    MenuPosition[]
  >([]);
  const [popupSections, setPopupSections] = useState<Section[]>([]);
  const showPopup = () => setShow(true);
  const hidePopup = () => setShow(false);

  return (
    <PopupContext.Provider
      value={{
        showPopup,
        hidePopup,
        setPopupItem,
        popupItem,
        popupVisible: show,
        popupCurrentItemSection,
        setPopupCurrentItemSection,
        popupSections,
        setPopupSections
      }}
    >
      {children}
    </PopupContext.Provider>
  );
}
